import { getIssueDisplayName, getIssueVariantTip } from './rule-issue-tips';

export class RuleDetailsPopupController {
	constructor(i18n, root) {
		this.__ = i18n;
		this.root = root;
		this.popupOverlay = null;
		this.popupTitle = null;
		this.popupBody = null;
	}

	toYesNo(value) {
		return value ? this.__( 'yes', 'a11yfix' ) : this.__( 'no', 'a11yfix' );
	}

	translatePartLabel(label) {
		const normalized = String(label || '').trim().toLowerCase();

		if (normalized === 'header') {
			return this.__( 'header', 'a11yfix' );
		}

		if (normalized === 'footer') {
			return this.__( 'footer', 'a11yfix' );
		}

		if (normalized === 'page') {
			return this.__( 'page', 'a11yfix' );
		}

		return String(label || '');
	}

	showRuleDetailsPopup(page, ruleKey) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		const config = this.getRuleModalConfig(ruleKey);

		if (!config) {
			return;
		}

		const items = this.getFailedRuleModalItems(page, ruleKey);
		this.popupTitle.textContent = `${config.title}: ${page.url}`;
		this.popupBody.innerHTML = '';

		if (items.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = config.emptyText;
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const summary = document.createElement('div');
		summary.className = 'a11yfix-popup-summary';
		summary.textContent = `${this.__( 'Failed items:', 'a11yfix' )} ${items.length}`;
		this.popupBody.appendChild(summary);

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list a11yfix-popup-list--compact';

		for (const itemData of items) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item a11yfix-popup-item--compact';

			const meta = document.createElement('div');
			meta.className = 'a11yfix-popup-meta';

			if (itemData.primary) {
				const primaryLine = document.createElement('p');
				primaryLine.className = 'a11yfix-popup-line';
				primaryLine.textContent = itemData.primary;
				meta.appendChild(primaryLine);
			}

			for (const detail of itemData.details || []) {
				if (!detail) {
					continue;
				}

				const detailLine = document.createElement('p');
				detailLine.className = 'a11yfix-popup-line';
				detailLine.textContent = detail;
				meta.appendChild(detailLine);
			}

			if (itemData.part) {
				const partLine = document.createElement('p');
				partLine.className = 'a11yfix-popup-line';
				partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${itemData.part}`;
				meta.appendChild(partLine);
			}

			if (itemData.tip) {
				const tipLine = document.createElement('p');
				tipLine.className = 'a11yfix-popup-line a11yfix-popup-line--tip';
				tipLine.textContent = `${this.__( 'Fix:', 'a11yfix' )} ${itemData.tip}`;
				meta.appendChild(tipLine);
			}

			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	getRuleModalConfig(ruleKey) {
		const map = {
			imageAlt: {
				title: this.__( 'Image alt issues', 'a11yfix' ),
				emptyText: this.__( 'No failed image alt results for this page.', 'a11yfix' )
			},
			linkTitle: {
				title: this.__( 'Link text issues', 'a11yfix' ),
				emptyText: this.__( 'No failed link results for this page.', 'a11yfix' )
			},
			headingOrder: {
				title: this.__( 'Heading issues', 'a11yfix' ),
				emptyText: this.__( 'No failed heading results for this page.', 'a11yfix' )
			},
			formLabels: {
				title: this.__( 'Form label issues', 'a11yfix' ),
				emptyText: this.__( 'No failed form label results for this page.', 'a11yfix' )
			},
			accessibleNamePreferredSource: {
				title: this.__( 'Accessible name source issues', 'a11yfix' ),
				emptyText: this.__( 'No failed accessible name source results for this page.', 'a11yfix' )
			},
			ariaLabelledbyIntegrity: {
				title: this.__( 'aria-labelledby integrity issues', 'a11yfix' ),
				emptyText: this.__( 'No failed aria-labelledby integrity results for this page.', 'a11yfix' )
			},
			ariaHiddenInteractive: {
				title: this.__( 'aria-hidden interactive issues', 'a11yfix' ),
				emptyText: this.__( 'No failed aria-hidden interactive results for this page.', 'a11yfix' )
			},
			keyboardFocusable: {
				title: this.__( 'Keyboard focusable issues', 'a11yfix' ),
				emptyText: this.__( 'No failed keyboard focusable results for this page.', 'a11yfix' )
			},
			lowColorContrast: {
				title: this.__( 'Low contrast issues', 'a11yfix' ),
				emptyText: this.__( 'No failed low contrast results for this page.', 'a11yfix' )
			}
		};

		return map?.[ruleKey] || null;
	}

	getFailedRuleModalItems(page, ruleKey) {
		if (ruleKey === 'imageAlt') {
			const details = page?.ruleDetails?.imageAlt || {};
			const invalidImages = Array.isArray(details.invalidImages)
				? details.invalidImages
				: (Array.isArray(details.images) ? details.images.filter((imageItem) => !String(imageItem?.alt || '').trim()) : []);

			return invalidImages.map((imageItem) => ({
				primary: `${this.__( 'Image:', 'a11yfix' )} ${imageItem?.src || this.__( 'No image source', 'a11yfix' )}`,
				details: [
					`${this.__( 'Alt:', 'a11yfix' )} ${imageItem?.alt || this.__( '(empty)', 'a11yfix' )}`
				],
				part: this.getSelectorPartsText(page, 'imageAlt', imageItem?.selector || ''),
				tip: getIssueVariantTip(this.__, 'imageAlt', '')
			}));
		}

		if (ruleKey === 'linkTitle') {
			const details = page?.ruleDetails?.linkTitle || {};
			const invalidLinks = Array.isArray(details.invalidLinks)
				? details.invalidLinks
				: (Array.isArray(details.links) ? details.links.filter((linkItem) => !String(linkItem?.text || '').trim()) : []);

			return invalidLinks.map((linkItem) => ({
				primary: `${this.__( 'Href:', 'a11yfix' )} ${linkItem?.href || this.__( '(empty)', 'a11yfix' )}`,
				details: [
					`${this.__( 'Text:', 'a11yfix' )} ${linkItem?.text || this.__( '(empty)', 'a11yfix' )}`
				],
				part: this.getSelectorPartsText(page, 'linkTitle', linkItem?.selector || ''),
				tip: getIssueVariantTip(this.__, 'linkTitle', '')
			}));
		}

		if (ruleKey === 'headingOrder') {
			const issues = Array.isArray(page?.ruleDetails?.headingOrder?.issues)
				? page.ruleDetails.headingOrder.issues
				: [];

			return issues.map((issueText) => ({
				primary: getIssueDisplayName(this.__, issueText, this.__( 'Heading issue', 'a11yfix' )),
				details: [],
				part: this.getIssuePartsText(page, 'headingOrder', issueText),
				tip: getIssueVariantTip(this.__, 'headingOrder', String(issueText || ''))
			}));
		}

		if (ruleKey === 'formLabels') {
			const controls = Array.isArray(page?.ruleDetails?.formLabels?.controls)
				? page.ruleDetails.formLabels.controls
				: [];
			const invalidControls = controls.filter((control) => control?.isValidLabel === false);

			return invalidControls.map((control) => ({
				primary: `${this.__( 'Control:', 'a11yfix' )} ${control?.tag || '-'} (${control?.type || '-'})`,
				details: [
					`${this.__( 'Issue:', 'a11yfix' )} ${control?.issue || this.__( 'Unknown issue', 'a11yfix' )}`,
					`${this.__( 'Label:', 'a11yfix' )} ${control?.labelValue || '-'}`
				],
				part: this.getSelectorPartsText(page, 'formLabels', control?.selector || ''),
				tip: getIssueVariantTip(this.__, 'formLabels', control?.issue || '')
			}));
		}

		if (ruleKey === 'accessibleNamePreferredSource') {
			const elements = Array.isArray(page?.ruleDetails?.accessibleNamePreferredSource?.issues)
				? page.ruleDetails.accessibleNamePreferredSource.issues
				: [];

			return elements.map((element) => ({
				primary: `${this.__( 'Element:', 'a11yfix' )} ${element?.tag || '-'} (${element?.type || '-'})`,
				details: [
					`${this.__( 'Issue:', 'a11yfix' )} ${element?.issue || this.__( 'Unknown issue', 'a11yfix' )}`,
					`${this.__( 'Visible name / aria-label:', 'a11yfix' )} ${element?.visibleNameValue || '-'} / ${element?.ariaLabel || '-'}`
				],
				part: this.getSelectorPartsText(page, 'accessibleNamePreferredSource', element?.selector || ''),
				tip: getIssueVariantTip(this.__, 'accessibleNamePreferredSource', element?.issue || '')
			}));
		}

		if (ruleKey === 'ariaLabelledbyIntegrity') {
			const elements = Array.isArray(page?.ruleDetails?.ariaLabelledbyIntegrity?.issues)
				? page.ruleDetails.ariaLabelledbyIntegrity.issues
				: [];

			return elements.map((element) => ({
				primary: `${this.__( 'Element:', 'a11yfix' )} ${element?.tag || '-'}`,
				details: [
					`${this.__( 'Issue:', 'a11yfix' )} ${element?.issue || this.__( 'Unknown issue', 'a11yfix' )}`,
					`${this.__( 'aria-labelledby:', 'a11yfix' )} ${element?.ariaLabelledby || '-'}`
				],
				part: this.getSelectorPartsText(page, 'ariaLabelledbyIntegrity', element?.selector || ''),
				tip: getIssueVariantTip(this.__, 'ariaLabelledbyIntegrity', element?.issue || '')
			}));
		}

		if (ruleKey === 'ariaHiddenInteractive') {
			const elements = Array.isArray(page?.ruleDetails?.ariaHiddenInteractive?.issues)
				? page.ruleDetails.ariaHiddenInteractive.issues
				: [];

			return elements.map((element) => ({
				primary: `${this.__( 'Element:', 'a11yfix' )} ${element?.tag || '-'}`,
				details: [
					`${this.__( 'Issue:', 'a11yfix' )} ${element?.issue || this.__( 'Unknown issue', 'a11yfix' )}`,
					`${this.__( 'aria-hidden:', 'a11yfix' )} ${element?.ariaHidden || '-'}`
				],
				part: this.getSelectorPartsText(page, 'ariaHiddenInteractive', element?.selector || ''),
				tip: getIssueVariantTip(this.__, 'ariaHiddenInteractive', element?.issue || '')
			}));
		}

		if (ruleKey === 'keyboardFocusable') {
			const elements = Array.isArray(page?.ruleDetails?.keyboardFocusable?.issues)
				? page.ruleDetails.keyboardFocusable.issues
				: [];

			return elements.map((element) => ({
				primary: `${this.__( 'Element:', 'a11yfix' )} ${element?.tag || '-'} (${element?.type || '-'})`,
				details: [
					`${this.__( 'Issue:', 'a11yfix' )} ${element?.issue || this.__( 'Unknown issue', 'a11yfix' )}`
				],
				part: this.getSelectorPartsText(page, 'keyboardFocusable', element?.selector || ''),
				tip: getIssueVariantTip(this.__, 'keyboardFocusable', element?.issue || '')
			}));
		}

		if (ruleKey === 'lowColorContrast') {
			const checks = Array.isArray(page?.ruleDetails?.lowColorContrast?.checks)
				? page.ruleDetails.lowColorContrast.checks
				: [];
			const issues = checks.filter((check) => check?.isFail === true);

			return issues.map((issue) => ({
				primary: `${this.__( 'Text:', 'a11yfix' )} ${issue?.text || '-'}`,
				details: [
					`${this.__( 'Contrast:', 'a11yfix' )} ${issue?.contrastRatio || '-'}:1 (${this.__( 'required', 'a11yfix' )} ${issue?.threshold || '-'}:1)`,
					`${this.__( 'Colors:', 'a11yfix' )} ${issue?.foreground || '-'} / ${issue?.background || '-'}`
				],
				part: this.getSelectorPartsText(page, 'lowColorContrast', issue?.selector || ''),
				tip: getIssueVariantTip(this.__, 'lowColorContrast', issue?.hasImageIntersection ? 'image-intersection' : '')
			}));
		}

		return [];
	}

	showImageAltPopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'Image alt details', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const images = page.ruleDetails?.imageAlt?.images || [];

		if (images.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No checked image data for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const imageItem of images) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const thumb = document.createElement('img');
			thumb.className = 'a11yfix-popup-thumb';
			thumb.src = imageItem.src || '';
			thumb.alt = imageItem.alt || this.__( 'No alt text', 'a11yfix' );

			const meta = document.createElement('div');
			const srcLine = document.createElement('p');
			const altLine = document.createElement('p');
			const partLine = document.createElement('p');

			srcLine.textContent = imageItem.src || this.__( 'No image source', 'a11yfix' );
			altLine.textContent = `${this.__( 'Alt:', 'a11yfix' )} ${imageItem.alt || this.__( '(empty)', 'a11yfix' )}`;
			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'imageAlt', imageItem.selector)}`;

			meta.appendChild(srcLine);
			meta.appendChild(altLine);
			meta.appendChild(partLine);

			item.appendChild(thumb);
			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	showLinksPopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'Links and text', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const links = page.ruleDetails?.linkTitle?.links || [];

		if (links.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No links found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const linkItem of links) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const meta = document.createElement('div');
			const hrefLine = document.createElement('p');
			const textLine = document.createElement('p');
			const partLine = document.createElement('p');

			hrefLine.textContent = `${this.__( 'Href:', 'a11yfix' )} ${linkItem.href || this.__( '(empty)', 'a11yfix' )}`;
			textLine.textContent = `${this.__( 'Text:', 'a11yfix' )} ${linkItem.text || this.__( '(empty)', 'a11yfix' )}`;
			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'linkTitle', linkItem.selector)}`;

			meta.appendChild(hrefLine);
			meta.appendChild(textLine);
			meta.appendChild(partLine);

			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	showHeadingPopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'Heading issues', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const issues = page.ruleDetails?.headingOrder?.issues || [];

		if (issues.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No heading problems found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const issue of issues) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const issueLine = document.createElement('p');
			const partLine = document.createElement('p');
			issueLine.textContent = issue;
			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getIssuePartsText(page, 'headingOrder', issue)}`;

			item.appendChild(issueLine);
			item.appendChild(partLine);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	showFormLabelsPopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'Form label issues', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const controls = page.ruleDetails?.formLabels?.controls || [];
		const invalidControls = controls.filter((control) => control.isValidLabel === false);
		const validControls = controls.filter((control) => control.isValidLabel === true);

		if (controls.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No form controls found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		if (invalidControls.length === 0) {
			const validState = document.createElement('p');
			validState.textContent = this.__( 'All form controls have valid labels.', 'a11yfix' );
			this.popupBody.appendChild(validState);
		}

		if (invalidControls.length > 0) {
			const invalidHeading = document.createElement('h3');
			invalidHeading.textContent = this.__( 'Invalid controls', 'a11yfix' );
			this.popupBody.appendChild(invalidHeading);
			this.popupBody.appendChild(this.createFormControlsList(page, invalidControls));
		}

		if (validControls.length > 0) {
			const validHeading = document.createElement('h3');
			validHeading.textContent = this.__( 'Valid controls', 'a11yfix' );
			this.popupBody.appendChild(validHeading);
			this.popupBody.appendChild(this.createFormControlsList(page, validControls));
		}

		this.popupOverlay.classList.add('is-open');
	}

	createFormControlsList(page, controls) {
		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const control of controls) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const meta = document.createElement('div');
			const controlLine = document.createElement('p');
			const contextLine = document.createElement('p');
			const labelLine = document.createElement('p');
			const issueLine = document.createElement('p');
			const partLine = document.createElement('p');

			controlLine.textContent = `${this.__( 'Control:', 'a11yfix' )} ${control.tag} (${control.type})`;
			contextLine.textContent = `${this.__( 'ID/Name:', 'a11yfix' )} ${control.id || '-'} / ${control.name || '-'}`;
			labelLine.textContent = `${this.__( 'Label source:', 'a11yfix' )} ${control.labelSource || '-'} | ${this.__( 'Label value:', 'a11yfix' )} ${control.labelValue || '-'}`;

			if (control.isValidLabel) {
				issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${this.__( 'None', 'a11yfix' )}`;
			} else {
				issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${control.issue || this.__( 'Unknown issue', 'a11yfix' )}`;
			}

			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'formLabels', control.selector)}`;

			meta.appendChild(controlLine);
			meta.appendChild(contextLine);
			meta.appendChild(labelLine);
			meta.appendChild(issueLine);
			meta.appendChild(partLine);

			item.appendChild(meta);
			list.appendChild(item);
		}

		return list;
	}

	showKeyboardFocusablePopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'Keyboard focusable elements', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const elements = page.ruleDetails?.keyboardFocusable?.elements || [];
		const issues = page.ruleDetails?.keyboardFocusable?.issues || [];
		const focusableElements = page.ruleDetails?.keyboardFocusable?.focusableElements || [];
		const dialogChecks = page.ruleDetails?.keyboardFocusable?.dialogChecks || [];
		const dialogIssues = page.ruleDetails?.keyboardFocusable?.dialogIssues || [];

		if (elements.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No keyboard-focusable candidates found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const summary = document.createElement('div');
		summary.className = 'a11yfix-popup-summary';
		summary.textContent = `${this.__( 'Candidates:', 'a11yfix' )} ${elements.length} | ${this.__( 'Focusable:', 'a11yfix' )} ${focusableElements.length} | ${this.__( 'Dialogs:', 'a11yfix' )} ${dialogChecks.length} | ${this.__( 'Issues:', 'a11yfix' )} ${issues.length}`;
		this.popupBody.appendChild(summary);

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const element of elements) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const meta = document.createElement('div');
			const nodeLine = document.createElement('p');
			const contextLine = document.createElement('p');
			const stateLine = document.createElement('p');
			const checksLine = document.createElement('p');
			const issueLine = document.createElement('p');
			const partLine = document.createElement('p');

			nodeLine.textContent = `${this.__( 'Element:', 'a11yfix' )} ${element.tag} (${element.type})`;
			contextLine.textContent = `${this.__( 'ID/Name/Role:', 'a11yfix' )} ${element.id || '-'} / ${element.name || '-'} / ${element.role || '-'}`;
			stateLine.textContent = `${this.__( 'State:', 'a11yfix' )} ${this.__( 'Focusable', 'a11yfix' )}: ${this.toYesNo(element.isKeyboardFocusable)} | tabindex: ${element.tabindex || '-'} | ${this.__( 'Disabled', 'a11yfix' )}: ${this.toYesNo(element.isDisabled)}`;
			checksLine.textContent = `${this.__( 'Checks:', 'a11yfix' )} ${this.__( 'Focus indicator', 'a11yfix' )}: ${this.toYesNo(element.hasVisibleFocusIndicator)} | ${this.__( 'Focus style removed', 'a11yfix' )}: ${this.toYesNo(element.hasSuppressedFocusStyle)} | ${this.__( 'CSS order override', 'a11yfix' )}: ${this.toYesNo(element.hasVisualOrderOverride)} | ${this.__( 'Custom key handlers', 'a11yfix' )}: ${element.needsExplicitKeyboardSupport ? this.toYesNo(element.hasExplicitKeyboardHandler) : '-'}`;

			if (element.issue) {
				issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${element.issue}`;
			} else {
				issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${this.__( 'None', 'a11yfix' )}`;
			}

			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'keyboardFocusable', element.selector)}`;

			meta.appendChild(nodeLine);
			meta.appendChild(contextLine);
			meta.appendChild(stateLine);
			meta.appendChild(checksLine);
			meta.appendChild(issueLine);
			meta.appendChild(partLine);

			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);

		if (dialogChecks.length > 0) {
			const dialogHeading = document.createElement('h3');
			dialogHeading.textContent = this.__( 'Dialog focus checks', 'a11yfix' );
			this.popupBody.appendChild(dialogHeading);

			const dialogList = document.createElement('ul');
			dialogList.className = 'a11yfix-popup-list';

			for (const dialogCheck of dialogChecks) {
				const item = document.createElement('li');
				item.className = 'a11yfix-popup-item';

				const meta = document.createElement('div');
				const dialogLine = document.createElement('p');
				const stateLine = document.createElement('p');
				const issueLine = document.createElement('p');
				const partLine = document.createElement('p');

				dialogLine.textContent = `${this.__( 'Dialog:', 'a11yfix' )} ${dialogCheck.tag} | ${this.__( 'ID/Role:', 'a11yfix' )} ${dialogCheck.id || '-'} / ${dialogCheck.role || '-'}`;
				stateLine.textContent = `${this.__( 'State:', 'a11yfix' )} aria-modal: ${dialogCheck.ariaModal || '-'} | ${this.__( 'Focusable elements', 'a11yfix' )}: ${dialogCheck.focusableCount} | ${this.__( 'Autofocus', 'a11yfix' )}: ${this.toYesNo(dialogCheck.hasAutofocusTarget)} | ${this.__( 'Container focusable', 'a11yfix' )}: ${this.toYesNo(dialogCheck.isContainerFocusable)}`;
				issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${dialogCheck.issue || this.__( 'None', 'a11yfix' )}`;
				partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'keyboardFocusable', dialogCheck.selector)}`;

				meta.appendChild(dialogLine);
				meta.appendChild(stateLine);
				meta.appendChild(issueLine);
				meta.appendChild(partLine);

				item.appendChild(meta);
				dialogList.appendChild(item);
			}

			this.popupBody.appendChild(dialogList);
		}

		this.popupOverlay.classList.add('is-open');
	}

	showAriaLabelledbyPopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'aria-labelledby integrity', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const ruleDetails = page.ruleDetails?.ariaLabelledbyIntegrity || {};
		const elements = ruleDetails?.elements || [];
		const issues = ruleDetails?.issues || [];

		if (ruleDetails?.error) {
			const errorState = document.createElement('p');
			errorState.textContent = `${this.__( 'Rule evaluation error:', 'a11yfix' )} ${ruleDetails.error}`;
			this.popupBody.appendChild(errorState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		if (elements.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No aria-labelledby usage found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const summary = document.createElement('div');
		summary.className = 'a11yfix-popup-summary';
		summary.textContent = `${this.__( 'Checked:', 'a11yfix' )} ${elements.length} | ${this.__( 'Issues:', 'a11yfix' )} ${issues.length}`;
		this.popupBody.appendChild(summary);

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const element of elements) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const meta = document.createElement('div');
			const nodeLine = document.createElement('p');
			const refsLine = document.createElement('p');
			const textLine = document.createElement('p');
			const issueLine = document.createElement('p');
			const partLine = document.createElement('p');

			nodeLine.textContent = `${this.__( 'Element:', 'a11yfix' )} ${element.tag} | ${this.__( 'ID/Name/Role:', 'a11yfix' )} ${element.id || '-'} / ${element.name || '-'} / ${element.role || '-'}`;
			refsLine.textContent = `${this.__( 'aria-labelledby:', 'a11yfix' )} ${element.ariaLabelledby || '-'} | ${this.__( 'Missing IDs:', 'a11yfix' )} ${(element.missingIds || []).join(', ') || '-'}`;
			textLine.textContent = `${this.__( 'Resolved text:', 'a11yfix' )} ${element.resolvedText || '-'} | ${this.__( 'Hidden references:', 'a11yfix' )} ${(element.hiddenReferenceIds || []).join(', ') || '-'}`;
			issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${element.issue || this.__( 'None', 'a11yfix' )}`;
			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'ariaLabelledbyIntegrity', element.selector)}`;

			meta.appendChild(nodeLine);
			meta.appendChild(refsLine);
			meta.appendChild(textLine);
			meta.appendChild(issueLine);
			meta.appendChild(partLine);

			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	showAccessibleNamePreferredSourcePopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'Accessible name source preference', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const ruleDetails = page.ruleDetails?.accessibleNamePreferredSource || {};
		const elements = ruleDetails?.elements || [];
		const issues = ruleDetails?.issues || [];

		if (ruleDetails?.error) {
			const errorState = document.createElement('p');
			errorState.textContent = `${this.__( 'Rule evaluation error:', 'a11yfix' )} ${ruleDetails.error}`;
			this.popupBody.appendChild(errorState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		if (elements.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No aria-label overrides with visible names found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const summary = document.createElement('div');
		summary.className = 'a11yfix-popup-summary';
		summary.textContent = `${this.__( 'Checked:', 'a11yfix' )} ${elements.length} | ${this.__( 'Issues:', 'a11yfix' )} ${issues.length}`;
		this.popupBody.appendChild(summary);

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const element of elements) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const meta = document.createElement('div');
			const nodeLine = document.createElement('p');
			const nameLine = document.createElement('p');
			const issueLine = document.createElement('p');
			const partLine = document.createElement('p');

			nodeLine.textContent = `${this.__( 'Element:', 'a11yfix' )} ${element.tag} (${element.type}) | ${this.__( 'ID/Name/Role:', 'a11yfix' )} ${element.id || '-'} / ${element.name || '-'} / ${element.role || '-'}`;
			nameLine.textContent = `${this.__( 'Visible name:', 'a11yfix' )} ${element.visibleNameValue || '-'} (${element.visibleNameSource || '-'}) | ${this.__( 'aria-label:', 'a11yfix' )} ${element.ariaLabel || '-'}`;
			issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${element.issue || this.__( 'None', 'a11yfix' )}`;
			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'accessibleNamePreferredSource', element.selector)}`;

			meta.appendChild(nodeLine);
			meta.appendChild(nameLine);
			meta.appendChild(issueLine);
			meta.appendChild(partLine);

			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	showAriaHiddenInteractivePopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'aria-hidden on interactive content', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const ruleDetails = page.ruleDetails?.ariaHiddenInteractive || {};
		const elements = ruleDetails?.elements || [];
		const issues = ruleDetails?.issues || [];

		if (ruleDetails?.error) {
			const errorState = document.createElement('p');
			errorState.textContent = `${this.__( 'Rule evaluation error:', 'a11yfix' )} ${ruleDetails.error}`;
			this.popupBody.appendChild(errorState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		if (elements.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No interactive candidates found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const summary = document.createElement('div');
		summary.className = 'a11yfix-popup-summary';
		summary.textContent = `${this.__( 'Checked:', 'a11yfix' )} ${elements.length} | ${this.__( 'Issues:', 'a11yfix' )} ${issues.length}`;
		this.popupBody.appendChild(summary);

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		for (const element of elements) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item';

			const meta = document.createElement('div');
			const nodeLine = document.createElement('p');
			const stateLine = document.createElement('p');
			const issueLine = document.createElement('p');
			const partLine = document.createElement('p');

			nodeLine.textContent = `${this.__( 'Element:', 'a11yfix' )} ${element.tag} | ${this.__( 'ID/Name/Role:', 'a11yfix' )} ${element.id || '-'} / ${element.name || '-'} / ${element.role || '-'}`;
			stateLine.textContent = `${this.__( 'aria-hidden:', 'a11yfix' )} ${element.ariaHidden || '-'} | ${this.__( 'Hidden ancestor:', 'a11yfix' )} ${element.hiddenAncestorSelector || '-'}`;
			issueLine.textContent = `${this.__( 'Issue:', 'a11yfix' )} ${element.issue || this.__( 'None', 'a11yfix' )}`;
			partLine.textContent = `${this.__( 'Part:', 'a11yfix' )} ${this.getSelectorPartsText(page, 'ariaHiddenInteractive', element.selector)}`;

			meta.appendChild(nodeLine);
			meta.appendChild(stateLine);
			meta.appendChild(issueLine);
			meta.appendChild(partLine);

			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	showLowContrastPopup(page) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = `${this.__( 'Low contrast details', 'a11yfix' )}: ${page.url}`;
		this.popupBody.innerHTML = '';

		const checks = page.ruleDetails?.lowColorContrast?.checks || [];
		const issues = checks.filter((check) => check.isFail === true);

		if (checks.length === 0) {
			const emptyState = document.createElement('p');
			emptyState.textContent = this.__( 'No visible text checks found for this page.', 'a11yfix' );
			this.popupBody.appendChild(emptyState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		if (issues.length === 0) {
			const validState = document.createElement('p');
			validState.textContent = this.__( 'No low contrast text found.', 'a11yfix' );
			this.popupBody.appendChild(validState);
			this.popupOverlay.classList.add('is-open');
			return;
		}

		const summary = document.createElement('div');
		summary.className = 'a11yfix-popup-summary';
		summary.textContent = `${this.__( 'Issues found:', 'a11yfix' )} ${issues.length}`;
		this.popupBody.appendChild(summary);

		const list = document.createElement('ul');
		list.className = 'a11yfix-popup-list';

		const createLine = (label, value, extraClass = '') => {
			const line = document.createElement('p');
			line.className = `a11yfix-popup-line${extraClass ? ` ${extraClass}` : ''}`;

			const labelNode = document.createElement('span');
			labelNode.className = 'a11yfix-popup-label';
			labelNode.textContent = `${label} `;

			const valueNode = document.createElement('span');
			valueNode.className = 'a11yfix-popup-value';
			valueNode.textContent = value;

			line.appendChild(labelNode);
			line.appendChild(valueNode);

			return line;
		};

		for (const issue of issues) {
			const item = document.createElement('li');
			item.className = 'a11yfix-popup-item a11yfix-popup-item-contrast';

			const meta = document.createElement('div');
			meta.className = 'a11yfix-popup-meta';

			const textLine = createLine(this.__( 'Text:', 'a11yfix' ), issue.text || '-');
			const ratioLine = createLine(
				this.__( 'Contrast:', 'a11yfix' ),
				`${issue.contrastRatio}:1 (${this.__( 'required', 'a11yfix' )} ${issue.threshold}:1)`
			);
			const colorLine = createLine(this.__( 'Colors:', 'a11yfix' ), `${issue.foreground} / ${issue.background}`);
			const partLine = createLine(this.__( 'Part:', 'a11yfix' ), this.getSelectorPartsText(page, 'lowColorContrast', issue.selector));
			const sourceLine = document.createElement('p');
			sourceLine.className = 'a11yfix-popup-line';

			const sourceLabel = document.createElement('span');
			sourceLabel.className = 'a11yfix-popup-label';
			sourceLabel.textContent = `${this.__( 'Background source:', 'a11yfix' )} `;

			const sourceValue = document.createElement('span');
			sourceValue.className = 'a11yfix-popup-source';

			if (issue.backgroundSource === 'solid-with-complex') {
				sourceValue.textContent = this.__( 'Solid color (complex background detected)', 'a11yfix' );
			} else {
				sourceValue.textContent = this.__( 'Solid color', 'a11yfix' );
			}

			sourceLine.appendChild(sourceLabel);
			sourceLine.appendChild(sourceValue);

			meta.appendChild(textLine);
			meta.appendChild(ratioLine);
			meta.appendChild(colorLine);
			meta.appendChild(partLine);
			meta.appendChild(sourceLine);

			if (issue.hasComplexBackground) {
				const noticeLine = createLine(
					this.__( 'Notice:', 'a11yfix' ),
					this.__( 'Background may include an image or gradient; result is based on solid background fallback.', 'a11yfix' ),
					'a11yfix-popup-line-notice'
				);
				meta.appendChild(noticeLine);
			}

			item.appendChild(meta);
			list.appendChild(item);
		}

		this.popupBody.appendChild(list);
		this.popupOverlay.classList.add('is-open');
	}

	getSelectorPartsText(page, ruleKey, selector) {
		const selectorParts = page?.rulePartsByRule?.[ruleKey]?.selectorParts;

		if (!selectorParts || typeof selectorParts !== 'object') {
			return this.__( 'page', 'a11yfix' );
		}

		const labels = selectorParts?.[selector];

		if (!Array.isArray(labels) || labels.length === 0) {
			return this.__( 'page', 'a11yfix' );
		}

		return labels.map((label) => this.translatePartLabel(label)).join(', ');
	}

	getIssuePartsText(page, ruleKey, issueText) {
		const failedItems = page?.ruleDetails?.[ruleKey]?.failedItems;

		if (!Array.isArray(failedItems) || failedItems.length === 0) {
			return this.__( 'page', 'a11yfix' );
		}

		const matchingItem = failedItems.find((item) => typeof item?.text === 'string' && item.text === issueText);
		return this.getSelectorPartsText(page, ruleKey, matchingItem?.selector || '');
	}

	showSubmitSamplePayloadPopup(samplePayload) {
		this.ensurePopup();

		if (!this.popupOverlay || !this.popupTitle || !this.popupBody) {
			return;
		}

		this.popupTitle.textContent = this.__( 'Submit sample payload preview', 'a11yfix' );
		this.popupBody.innerHTML = '';

		const intro = document.createElement('p');
		intro.textContent = this.__( 'This preview contains only anonymous technical mapping data prepared for live/edit mismatch tuning.', 'a11yfix' );

		const focusHeading = document.createElement('h3');
		focusHeading.textContent = this.__( 'Why this anonymous sample is useful', 'a11yfix' );

		const focusDescription = document.createElement('p');
		focusDescription.textContent = this.__( 'Low-confidence mappings are the most helpful for improving the live/edit block mapping algorithm. Thank you very much for sharing this sample and helping make results better for everyone.', 'a11yfix' );

		const focusPre = document.createElement('pre');
		focusPre.className = 'a11yfix-popup-json-preview';
		focusPre.style.whiteSpace = 'pre-wrap';
		focusPre.style.wordBreak = 'break-word';
		focusPre.style.maxHeight = '320px';
		focusPre.style.overflow = 'auto';
		focusPre.style.padding = '12px';
		focusPre.style.background = '#f6f7f7';
		focusPre.style.border = '1px solid #dcdcde';
		focusPre.style.borderRadius = '4px';
		focusPre.textContent = JSON.stringify(this.buildSubmitSampleFocusView(samplePayload), null, 2);

		const pre = document.createElement('pre');
		pre.className = 'a11yfix-popup-json-preview';
		pre.style.whiteSpace = 'pre-wrap';
		pre.style.wordBreak = 'break-word';
		pre.style.maxHeight = '420px';
		pre.style.overflow = 'auto';
		pre.style.padding = '12px';
		pre.style.background = '#f6f7f7';
		pre.style.border = '1px solid #dcdcde';
		pre.style.borderRadius = '4px';
		pre.textContent = JSON.stringify(samplePayload || {}, null, 2);

		const details = document.createElement('details');
		details.className = 'a11yfix-popup-json-details';

		const summary = document.createElement('summary');
		summary.textContent = this.__( 'Full payload JSON', 'a11yfix' );

		details.appendChild(summary);
		details.appendChild(pre);

		this.popupBody.appendChild(intro);
		this.popupBody.appendChild(focusHeading);
		this.popupBody.appendChild(focusDescription);
		this.popupBody.appendChild(focusPre);
		this.popupBody.appendChild(details);
		this.popupOverlay.classList.add('is-open');
	}

	buildSubmitSampleFocusView(samplePayload) {
		const payload = samplePayload && typeof samplePayload === 'object' ? samplePayload : {};
		const mappingPairs = payload.mappingPairs && typeof payload.mappingPairs === 'object' ? payload.mappingPairs : {};
		const rawMatches = Array.isArray(mappingPairs.matches) ? mappingPairs.matches : [];
		const unmatchedLiveBlocks = Array.isArray(mappingPairs.unmatchedLiveBlocks) ? mappingPairs.unmatchedLiveBlocks : [];
		const unmatchedEditBlocks = Array.isArray(mappingPairs.unmatchedEditBlocks) ? mappingPairs.unmatchedEditBlocks : [];

		const lowConfidenceMatches = rawMatches
			.filter((match) => {
				const score = Number(match?.score) || 0;
				const threshold = Number(match?.threshold) || 0;
				const scoreGap = Number(match?.scoreGap) || 0;

				return score <= threshold || scoreGap <= 1;
			})
			.sort((left, right) => {
				const leftGap = Number(left?.scoreGap) || 0;
				const rightGap = Number(right?.scoreGap) || 0;
				if (leftGap !== rightGap) {
					return leftGap - rightGap;
				}

				const leftScore = Number(left?.score) || 0;
				const rightScore = Number(right?.score) || 0;
				return leftScore - rightScore;
			})
			.slice(0, 60)
			.map((match) => ({
				liveIndex: Number.isInteger(match?.liveIndex) ? match.liveIndex : -1,
				editorIndex: Number.isInteger(match?.editorIndex) ? match.editorIndex : -1,
				blockType: String(match?.blockType || 'unknown'),
				score: Number(match?.score) || 0,
				threshold: Number(match?.threshold) || 0,
				scoreGap: Number(match?.scoreGap) || 0,
				liveSelector: String(match?.live?.selector || ''),
				editSelector: String(match?.edit?.selector || ''),
				liveClasses: String(match?.live?.className || ''),
				editClasses: String(match?.edit?.className || ''),
				liveTextHash: String(match?.live?.textHash || ''),
				editTextHash: String(match?.edit?.textHash || '')
			}));

		return {
			generatedAt: payload.generatedAt || '',
			readiness: payload.readiness || {},
			mappingAlgorithmInputs: payload.mappingAlgorithmInputs || {},
			reviewCounts: {
				lowConfidenceMatches: lowConfidenceMatches.length,
				unmatchedLiveBlocks: unmatchedLiveBlocks.length,
				unmatchedEditBlocks: unmatchedEditBlocks.length
			},
			lowConfidenceMatches,
			unmatchedLiveBlocks: unmatchedLiveBlocks.slice(0, 60),
			unmatchedEditBlocks: unmatchedEditBlocks.slice(0, 60)
		};
	}

	ensurePopup() {
		if (this.popupOverlay || !this.root) {
			return;
		}

		const overlay = document.createElement('div');
		overlay.className = 'a11yfix-popup-overlay';

		const popup = document.createElement('div');
		popup.className = 'a11yfix-popup';

		const header = document.createElement('div');
		header.className = 'a11yfix-popup-header';

		const title = document.createElement('h2');
		title.className = 'a11yfix-popup-title';

		const closeButton = document.createElement('button');
		closeButton.type = 'button';
		closeButton.className = 'button';
		closeButton.textContent = this.__( 'Close', 'a11yfix' );

		const body = document.createElement('div');
		body.className = 'a11yfix-popup-body';

		header.appendChild(title);
		header.appendChild(closeButton);
		popup.appendChild(header);
		popup.appendChild(body);
		overlay.appendChild(popup);
		this.root.appendChild(overlay);

		closeButton.addEventListener('click', () => {
			overlay.classList.remove('is-open');
		});

		overlay.addEventListener('click', (event) => {
			if (event.target === overlay) {
				overlay.classList.remove('is-open');
			}
		});

		this.popupOverlay = overlay;
		this.popupTitle = title;
		this.popupBody = body;
	}
}
